import { Content, InlineEditItem } from '@amzn/awscat-pattern-library-service-model';
import { log } from '../loggers/Logger';
import { deepCloneContent } from '../model/CloneContent';
import { sanitizeInlineEditReplacement, SanitizedNode } from './InlineEditSanitizer';

/** An inline edit that is ready to be applied onto a ProseMirror document */
interface UnappliedInlineEdit {
  readonly editId: string;
  readonly replacement: SanitizedNode;
}

/** A ProseMirror node that has a list of child nodes. */
interface ProseMirrorParentNode {
  readonly content: unknown[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function hasChildNodes(value: unknown): value is ProseMirrorParentNode {
  return isRecord(value) && Array.isArray(value.content);
}

function isTiptapDoc(value: unknown): value is ProseMirrorParentNode & { readonly type: 'doc' } {
  return isRecord(value) && value.type === 'doc' && hasChildNodes(value);
}

/**
 * Removes and returns the unapplied edit that targets the given node.
 *
 * @returns the edit to be applied, or undefined when no edit targets it.
 */
function takeEditForNode(
  node: Record<string, unknown>,
  unappliedEditsByNodeId: Map<string, UnappliedInlineEdit>,
): UnappliedInlineEdit | undefined {
  const nodeId = isRecord(node.attrs) ? node.attrs.nodeId : undefined;
  if (typeof nodeId !== 'string') return undefined;

  const unapplied = unappliedEditsByNodeId.get(nodeId);
  unappliedEditsByNodeId.delete(nodeId);
  return unapplied;
}

/**
 * Walks a ProseMirror doc tree in place. Any node whose `attrs.nodeId` matches
 * an unapplied inline edit is swapped for the edit's sanitized replacement, and
 * the matched entry is removed from the map — leftovers after the full
 * traversal are the edits whose target nodeId never appeared anywhere.
 */
function replaceMatchingNodes(
  parentNode: ProseMirrorParentNode,
  unappliedEditsByNodeId: Map<string, UnappliedInlineEdit>,
): void {
  const children = parentNode.content;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (!isRecord(child)) continue;

    // Replace in place when the child itself matches; the fresh subtree carries
    // its own node ids, so we skip recursing into it to avoid aliased writes.
    const unapplied = takeEditForNode(child, unappliedEditsByNodeId);
    if (unapplied) {
      children[i] = unapplied.replacement;
      continue;
    }

    // Leaf nodes such as text have no children to search.
    if (hasChildNodes(child)) replaceMatchingNodes(child, unappliedEditsByNodeId);
  }
}

/**
 * Walks any Content substructure until it reaches a tiptap doc, then replaces
 * matching PM nodes inside it.
 */
function applyEditsRecursively(
  value: unknown,
  unappliedEditsByNodeId: Map<string, UnappliedInlineEdit>,
): void {
  if (isTiptapDoc(value)) {
    replaceMatchingNodes(value, unappliedEditsByNodeId);
    return;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      applyEditsRecursively(item, unappliedEditsByNodeId);
    }
    return;
  }

  if (isRecord(value)) {
    for (const nested of Object.values(value)) {
      applyEditsRecursively(nested, unappliedEditsByNodeId);
    }
  }
}

/**
 * Applies unresolved inline edits to a deep-cloned copy of the content.
 *
 * When sanitization throws or the target node id is missing, the edit is skipped and
 * logged at warn level. Use when the user accepts all suggested changes at once.
 */
export function applyInlineEditsToContent(
  content: Content,
  edits: readonly InlineEditItem[],
): Content {
  const cloned = deepCloneContent(content);

  // Sanitize each unresolved edit up front and index by nodeId so the single
  // traversal below can match in O(1) and apply all edits in one pass.
  const unappliedEditsByNodeId = new Map<string, UnappliedInlineEdit>();
  for (const edit of edits) {
    if (edit.resolved) continue;
    try {
      unappliedEditsByNodeId.set(edit.nodeId, {
        editId: edit.editId,
        replacement: sanitizeInlineEditReplacement(edit.replacement),
      });
    } catch (err) {
      log.warn(
        { err, editId: edit.editId },
        '[applyInlineEditsToContent] sanitizer rejected replacement — skipping edit',
      );
    }
  }

  applyEditsRecursively(cloned.contentData, unappliedEditsByNodeId);

  // Leftover entries target node ids that no longer exist in the content.
  if (unappliedEditsByNodeId.size > 0) {
    log.warn(
      {
        editIds: Array.from(unappliedEditsByNodeId.values(), (e) => e.editId),
        nodeIds: Array.from(unappliedEditsByNodeId.keys()),
      },
      `[applyInlineEditsToContent] ${unappliedEditsByNodeId.size} edit(s) not applied because their target node was not found`,
    );
  }

  return cloned;
}
